import {
  Criteria,
  DeviceAssignmentsCriteria,
  DeviceAssignmentsTableRow,
  DeviceData,
  DeviceGroupsCriteria,
  DeviceHomeAlertData,
  DeviceModel,
  MyDevicesData,
  PaginatedAPIResponse,
} from '@zspace/types';
import { http } from '../shared/http';
import { filterNullishCriteria } from '../shared/utils';

export interface DevicesService {
  fetchDeviceModels: (
    criteria: Criteria
  ) => Promise<PaginatedAPIResponse<DeviceModel>>;
  fetchMyDevices: (criteria: DeviceGroupsCriteria) => Promise<MyDevicesData>;
  fetchDeviceById: (id: string) => Promise<DeviceData>;
  fetchDeviceAssignments: (
    id: string,
    criteria: DeviceAssignmentsCriteria
  ) => Promise<PaginatedAPIResponse<DeviceAssignmentsTableRow>>;
  registerDevice: (
    serialNumber: string,
    approvalUrlCode: string
  ) => Promise<void>;
  fetchDevicesHomeAlertData: () => Promise<DeviceHomeAlertData>;
}

export class DevicesServiceImpl implements DevicesService {
  public async fetchDeviceModels(
    criteria: Criteria
  ): Promise<PaginatedAPIResponse<DeviceModel>> {
    const { data } = await http(
      '/v1/devices/models?' +
        new URLSearchParams(criteria as unknown as Record<string, string>)
    );

    return data;
  }

  public async fetchMyDevices(
    criteria: DeviceGroupsCriteria
  ): Promise<MyDevicesData> {
    const { data } = await http(
      '/v1/devices?' +
        new URLSearchParams(criteria as unknown as Record<string, string>)
    );

    return data;
  }

  public async fetchDeviceById(id: string): Promise<DeviceData> {
    const { data } = await http(`/v1/devices/${id}`);

    return data;
  }

  public async fetchDeviceAssignments(
    id: string,
    criteria: DeviceAssignmentsCriteria
  ): Promise<PaginatedAPIResponse<DeviceAssignmentsTableRow>> {
    const { data } = await http(
      `/v1/devices/${id}/assignments?` +
        new URLSearchParams(criteria as unknown as Record<string, string>)
    );

    return data;
  }

  public async registerDevice(serialNumber: string, approvalUrlCode: string) {
    const params = {
      serialNumber,
      approvalUrlCode,
    };
    await http.post(
      '/v1/devices?' +
        new URLSearchParams(params as unknown as Record<string, string>)
    );
  }

  public async fetchDevicesHomeAlertData(): Promise<DeviceHomeAlertData> {
    const { data } = await http('/v1/devices/home-alerts');
    return data;
  }
}

export const fetchDeviceModels = async (
  criteria: Criteria
): Promise<PaginatedAPIResponse<DeviceModel>> => {
  const service = new DevicesServiceImpl();

  return service.fetchDeviceModels(filterNullishCriteria(criteria));
};

export const fetchMyDevices = async (
  criteria: DeviceGroupsCriteria
): Promise<MyDevicesData> => {
  const service = new DevicesServiceImpl();

  return service.fetchMyDevices(filterNullishCriteria(criteria));
};

export const fetchDeviceById = async (id: string): Promise<DeviceData> => {
  const service = new DevicesServiceImpl();

  return service.fetchDeviceById(id);
};

export const fetchDeviceAssignments = async (
  id: string,
  criteria: DeviceAssignmentsCriteria
): Promise<PaginatedAPIResponse<DeviceAssignmentsTableRow>> => {
  const service = new DevicesServiceImpl();

  return service.fetchDeviceAssignments(id, filterNullishCriteria(criteria));
};

export const registerDevice = async (
  serialNumber: string,
  approvalUrlCode: string
): Promise<void> => {
  const service = new DevicesServiceImpl();

  return service.registerDevice(serialNumber, approvalUrlCode);
};

export const fetchDevicesHomeAlertData =
  async (): Promise<DeviceHomeAlertData> => {
    const service = new DevicesServiceImpl();

    return service.fetchDevicesHomeAlertData();
  };
